import {logError} from '../fingerprint.js';
import {countTruthy} from './helpers.js';

const MEDIA_QUERY_VALUES = {
    INVERTED_COLORS: ['none', 'inverted'],
    FORCED_COLORS: ['none', 'active'],
    PREFERS_CONTRAST: ['no-preference', 'high', 'more', 'less'],
    REDUCED_MOTION: ['no-preference', 'reduce'],
    DYNAMIC_RANGE: ['standard', 'high'],
    COLOR_GAMUT: ['rec2020', 'p3', 'srgb'],
};

function detectMsFamily(window) {
    const navigator = window.navigator;
    return countTruthy([
        'MSCSSMatrix' in window,
        'msSetImmediate' in window,
        'msIndexedDB' in window,
        'msMaxTouchPoints' in navigator,
        'msPointerEnabled' in navigator,
    ]) >= 4;
}

function detectChromiumLike(window) {
    const navigator = window.navigator;
    return countTruthy([
        'webkitPersistentStorage' in navigator,
        'webkitTemporaryStorage' in navigator,
        typeof navigator.vendor === 'string' && navigator.vendor.indexOf('Google') === 0,
        'webkitResolveLocalFileSystemURL' in window,
        'BatteryManager' in window,
        'webkitMediaStream' in window,
        'webkitSpeechGrammar' in window,
    ]) >= 5;
}

function detectAppleWebKit(window) {
    const navigator = window.navigator;
    return countTruthy([
        'ApplePayError' in window,
        'CSSPrimitiveValue' in window,
        'Counter' in window,
        typeof navigator.vendor === 'string' && navigator.vendor.indexOf('Apple') === 0,
        'getStorageUpdates' in navigator,
        'WebKitMediaKeys' in window,
    ]) >= 4;
}

function detectSafariLike(window) {
    const HTMLElement = window.HTMLElement;
    const Document = window.Document;
    return countTruthy([
        'safari' in window,
        !('ongestureend' in window),
        !('TouchEvent' in window),
        !('orientation' in window),
        HTMLElement && !('autocapitalize' in HTMLElement.prototype),
        Document && 'pointerLockElement' in Document.prototype,
    ]) >= 4;
}

function detectYandexLike(window) {
    const isNative = fn => /\[native code]/.test(String(fn));
    return Boolean(
        isNative(window.print) &&
        countTruthy([
            String(window.browser) === '[object WebPageNamespace]',
            'MicrodataExtractor' in window,
        ]) >= 1,
    );
}

function detectModernCss(window) {
    const navigator = window.navigator;
    const CSS = window.CSS;
    const HTMLButtonElement = window.HTMLButtonElement;
    return countTruthy([
        !('getStorageUpdates' in navigator),
        HTMLButtonElement && 'popover' in HTMLButtonElement.prototype,
        'CSSCounterStyleRule' in window,
        CSS && CSS.supports('font-size-adjust: ex-height 0.5'),
        CSS && CSS.supports('text-transform: full-width'),
    ]) >= 4;
}

function matchMediaQuery(window, query) {
    try {
        return window.matchMedia(query).matches;
    } catch {
        return false;
    }
}

function detectColorGamut(window) {
    try {
        for (let i = 0; i < MEDIA_QUERY_VALUES.COLOR_GAMUT.length; i++) {
            const value = MEDIA_QUERY_VALUES.COLOR_GAMUT[i];
            if (matchMediaQuery(window, `(color-gamut: ${value})`)) {
                return value;
            }
        }
    } catch (error) {
        logError(error);
    }
    return null;
}

function detectEffectiveMonochrome(window) {
    try {
        if (!matchMediaQuery(window, '(min-monochrome: 0)')) {
            return null;
        }
        for (let i = 0; i <= 100; i++) {
            if (matchMediaQuery(window, `(max-monochrome: ${i})`)) {
                return i;
            }
        }
    } catch (error) {
        logError(error);
    }
    return null;
}

function detectContrastPreference(window) {
    try {
        if (matchMediaQuery(window, '(prefers-contrast: no-preference)')) {
            return 0;
        }
        if (matchMediaQuery(window, '(prefers-contrast: high)') || matchMediaQuery(window, '(prefers-contrast: more)')) {
            return 1;
        }
        if (matchMediaQuery(window, '(prefers-contrast: low)') || matchMediaQuery(window, '(prefers-contrast: less)')) {
            return -1;
        }
        if (matchMediaQuery(window, '(prefers-contrast: forced)')) {
            return 10;
        }
    } catch (error) {
        logError(error);
    }
    return null;
}

function detectHighDynamicRange(window) {
    try {
        if (matchMediaQuery(window, '(dynamic-range: high)')) {
            return true;
        }
        if (!matchMediaQuery(window, '(dynamic-range: standard)')) {
            return null;
        }
        return false;
    } catch (error) {
        logError(error);
        return null;
    }
}

function detectReducedMotion(window) {
    try {
        if (matchMediaQuery(window, '(prefers-reduced-motion: reduce)')) {
            return true;
        }
        if (!matchMediaQuery(window, '(prefers-reduced-motion: no-preference)')) {
            return null;
        }
        return false;
    } catch (error) {
        logError(error);
        return null;
    }
}

function readScreenFrame(window) {
    const screen = window.screen;
    if (!screen) {
        return [null, null, null, null];
    }

    const availTop = Number.isFinite(screen.availTop) ? screen.availTop : null;
    const availLeft = Number.isFinite(screen.availLeft) ? screen.availLeft : null;
    const availHeight = Number.isFinite(screen.availHeight) ? screen.availHeight : null;
    const availWidth = Number.isFinite(screen.availWidth) ? screen.availWidth : null;
    const width = Number.isFinite(screen.width) ? screen.width : null;
    const height = Number.isFinite(screen.height) ? screen.height : null;

    const topInset = availTop;
    const leftInset = availLeft;
    const rightInset = typeof width === 'number' && typeof availWidth === 'number' && typeof availLeft === 'number'
        ? width - availWidth - availLeft
        : null;
    const bottomInset = typeof height === 'number' && typeof availHeight === 'number' && typeof availTop === 'number'
        ? height - availHeight - availTop
        : null;

    return [topInset, rightInset, bottomInset, leftInset];
}

function areZeroInsets(insets) {
    for (let i = 0; i < insets.length; i++) {
        if (insets[i]) {
            return false;
        }
    }
    return true;
}

function buildMediaSnapshot(window) {
    const snapshot = {
        invertedColors: {},
        forcedColors: {},
        prefersContrast: {},
        reducedMotion: {},
        dynamicRange: {},
        colorGamut: detectColorGamut(window),
        contrastPreferenceScore: detectContrastPreference(window),
        effectiveMonochrome: detectEffectiveMonochrome(window),
        highDynamicRange: detectHighDynamicRange(window),
        prefersReducedMotion: detectReducedMotion(window),
    };

    MEDIA_QUERY_VALUES.INVERTED_COLORS.forEach(value => {
        snapshot.invertedColors[value] = matchMediaQuery(window, `(inverted-colors: ${value})`);
    });

    MEDIA_QUERY_VALUES.FORCED_COLORS.forEach(value => {
        snapshot.forcedColors[value] = matchMediaQuery(window, `(forced-colors: ${value})`);
    });

    MEDIA_QUERY_VALUES.PREFERS_CONTRAST.forEach(value => {
        snapshot.prefersContrast[value.replace('-', '')] = matchMediaQuery(window, `(prefers-contrast: ${value})`);
    });

    MEDIA_QUERY_VALUES.REDUCED_MOTION.forEach(value => {
        const key = value === 'no-preference' ? 'noPreference' : value;
        snapshot.reducedMotion[key] = matchMediaQuery(window, `(prefers-reduced-motion: ${value})`);
    });

    MEDIA_QUERY_VALUES.DYNAMIC_RANGE.forEach(value => {
        snapshot.dynamicRange[value] = matchMediaQuery(window, `(dynamic-range: ${value})`);
    });

    return snapshot;
}

function buildEngineSnapshot(window) {
    return {
        isMsFamily: detectMsFamily(window),
        isChromiumLike: detectChromiumLike(window),
        isAppleWebKit: detectAppleWebKit(window),
        isSafariLike: detectSafariLike(window),
        isYandexLike: detectYandexLike(window),
        hasModernCss: detectModernCss(window),
    };
}

function buildScreenSnapshot(window) {
    const insets = readScreenFrame(window);
    return {
        width: Number.isFinite(window.screen?.width) ? window.screen.width : null,
        height: Number.isFinite(window.screen?.height) ? window.screen.height : null,
        availWidth: Number.isFinite(window.screen?.availWidth) ? window.screen.availWidth : null,
        availHeight: Number.isFinite(window.screen?.availHeight) ? window.screen.availHeight : null,
        devicePixelRatio: Number.isFinite(window.devicePixelRatio) ? window.devicePixelRatio : null,
        insets,
        insetsZero: areZeroInsets(insets),
    };
}

/**
 * Собирает эвристики по типу движка, медиа-настройкам и параметрам экрана.
 * @param {Window} window
 * @returns {Promise<IFeatureDetectors>}
 */
export default async function collectFeatureDetectors(window) {
    const emptyResult = {
        featureDetectors: {
            engines: {},
            media: {},
            screen: {},
        },
    };

    if (!window) {
        return emptyResult;
    }

    try {
        return {
            featureDetectors: {
                engines: buildEngineSnapshot(window),
                media: buildMediaSnapshot(window),
                screen: buildScreenSnapshot(window),
            },
        };
    } catch (error) {
        logError(error);
        return emptyResult;
    }
}
